import { useEffect, useState, useCallback } from 'react'
import { Database, Plus, X, Trash2, RefreshCw, CheckCircle2, XCircle } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Bcp {
  id: number
  service: string
  tier: string
  declared_rto_minutes: number
  declared_rpo_minutes: number
  last_tested_rto_minutes: number | null
  last_tested_rpo_minutes: number | null
  last_tested_at: string | null
  test_cadence_days: number
  backup_strategy: string
  notes: string
  freshness: string
  rto_met: boolean
  rpo_met: boolean
}

const TIER_TONE: Record<string, string> = {
  'tier-1': 'bg-rose/15 text-rose border-rose/30',
  'tier-2': 'bg-amber/15 text-amber-light border-amber/30',
  'tier-3': 'bg-blue/15 text-blue-light border-blue/30',
}
const FRESH_TONE: Record<string, string> = {
  fresh: 'text-emerald', stale: 'text-amber-light', overdue: 'text-rose', untested: 'text-text-muted',
}

function fmtMin(m: number | null): string {
  if (m == null) return '—'
  if (m < 60) return `${m}m`
  if (m < 1440) return `${(m / 60).toFixed(1)}h`
  return `${(m / 1440).toFixed(1)}d`
}

export default function BcpPage() {
  const [items, setItems] = useState<Bcp[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({
    service: '', tier: 'tier-2',
    declared_rto_minutes: 240, declared_rpo_minutes: 60,
    last_tested_rto_minutes: '', last_tested_rpo_minutes: '',
    last_tested_at: '', test_cadence_days: 365,
    backup_strategy: '', notes: '',
  })

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get('/api/res/bcp')
      setItems(r.data?.entries || [])
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  const create = async () => {
    if (!form.service) return
    await api.post('/api/res/bcp', {
      ...form,
      last_tested_rto_minutes: form.last_tested_rto_minutes ? parseInt(form.last_tested_rto_minutes, 10) : undefined,
      last_tested_rpo_minutes: form.last_tested_rpo_minutes ? parseInt(form.last_tested_rpo_minutes, 10) : undefined,
      last_tested_at: form.last_tested_at ? new Date(form.last_tested_at).toISOString() : undefined,
    })
    setShowForm(false)
    setForm({ ...form, service: '', backup_strategy: '', notes: '' })
    fetchAll()
  }

  const remove = async (id: number) => {
    if (!confirm('Delete entry?')) return
    await api.delete(`/api/res/bcp/${id}`)
    fetchAll()
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Database size={24} className="text-blue-light" /> BCP / DR Register
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Per-service RTO + RPO declared vs last-tested. Compliance shown per row.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={fetchAll} className="btn-ghost text-xs"><RefreshCw size={11} className={loading ? 'animate-spin' : ''} /></button>
          <button type="button" onClick={() => setShowForm(true)} className="btn-primary text-xs"><Plus size={12} /> Add service</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Total services" value={items.length} tone="blue" />
        <Tile label="RTO met" value={items.filter(i => i.rto_met).length} tone="emerald" />
        <Tile label="RTO missed" value={items.filter(i => !i.rto_met && i.last_tested_rto_minutes != null).length} tone="rose" pulse={items.some(i => !i.rto_met && i.last_tested_rto_minutes != null)} />
        <Tile label="Untested" value={items.filter(i => i.freshness === 'untested').length} tone="amber" />
      </div>

      {showForm && (
        <div className="card p-4 border border-blue/40">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-text-primary">BCP entry</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-text-muted hover:text-text-primary"><X size={14} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
            <input value={form.service} onChange={e => setForm({...form, service: e.target.value})} placeholder="Service" className="input py-1.5" />
            <select value={form.tier} onChange={e => setForm({...form, tier: e.target.value})} className="input py-1.5">
              <option value="tier-1">tier-1 (critical)</option>
              <option value="tier-2">tier-2 (important)</option>
              <option value="tier-3">tier-3 (deferrable)</option>
            </select>
            <input type="number" value={form.declared_rto_minutes} onChange={e => setForm({...form, declared_rto_minutes: parseInt(e.target.value, 10)})} placeholder="Declared RTO (min)" className="input py-1.5" />
            <input type="number" value={form.declared_rpo_minutes} onChange={e => setForm({...form, declared_rpo_minutes: parseInt(e.target.value, 10)})} placeholder="Declared RPO (min)" className="input py-1.5" />
            <input type="number" value={form.last_tested_rto_minutes} onChange={e => setForm({...form, last_tested_rto_minutes: e.target.value})} placeholder="Last tested RTO (min)" className="input py-1.5" />
            <input type="number" value={form.last_tested_rpo_minutes} onChange={e => setForm({...form, last_tested_rpo_minutes: e.target.value})} placeholder="Last tested RPO (min)" className="input py-1.5" />
            <label className="space-y-0.5"><span className="text-text-muted">Last tested at</span>
              <input type="datetime-local" value={form.last_tested_at} onChange={e => setForm({...form, last_tested_at: e.target.value})} className="input w-full py-1.5" /></label>
            <input type="number" value={form.test_cadence_days} onChange={e => setForm({...form, test_cadence_days: parseInt(e.target.value, 10)})} placeholder="Test cadence (days)" className="input py-1.5" />
            <textarea value={form.backup_strategy} onChange={e => setForm({...form, backup_strategy: e.target.value})} rows={2} placeholder="Backup strategy" className="input py-1.5 md:col-span-2" />
            <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={1} placeholder="Notes" className="input py-1.5 md:col-span-2" />
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button type="button" onClick={() => setShowForm(false)} className="btn-ghost text-xs">Cancel</button>
            <button type="button" onClick={create} className="btn-primary text-xs">Save</button>
          </div>
        </div>
      )}

      <div className="card p-3 overflow-x-auto">
        {items.length === 0 ? (
          <p className="text-center py-6 text-text-muted text-[11px] italic">No services registered.</p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-text-muted border-b border-border">
                <th className="text-left py-2 px-2">Service</th>
                <th className="text-left py-2 px-2">Tier</th>
                <th className="text-right py-2 px-2">Declared RTO</th>
                <th className="text-right py-2 px-2">Tested RTO</th>
                <th className="text-right py-2 px-2">Declared RPO</th>
                <th className="text-right py-2 px-2">Tested RPO</th>
                <th className="text-left py-2 px-2">Last tested</th>
                <th className="text-right py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(b => (
                <tr key={b.id} className="border-b border-border/50 hover:bg-surface-hover">
                  <td className="py-1.5 px-2">
                    <div className="text-text-primary font-medium">{b.service}</div>
                    {b.backup_strategy && <div className="text-[10px] text-text-muted truncate max-w-[200px]">{b.backup_strategy}</div>}
                  </td>
                  <td className="py-1.5 px-2">
                    <span className={clsx('badge text-[9px] uppercase border', TIER_TONE[b.tier])}>{b.tier}</span>
                  </td>
                  <td className="py-1.5 px-2 text-right font-mono text-text-secondary">{fmtMin(b.declared_rto_minutes)}</td>
                  <td className="py-1.5 px-2 text-right font-mono">
                    {b.last_tested_rto_minutes != null && (
                      <span className={clsx('inline-flex items-center gap-1', b.rto_met ? 'text-emerald' : 'text-rose')}>
                        {b.rto_met ? <CheckCircle2 size={10} /> : <XCircle size={10} />}
                        {fmtMin(b.last_tested_rto_minutes)}
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 px-2 text-right font-mono text-text-secondary">{fmtMin(b.declared_rpo_minutes)}</td>
                  <td className="py-1.5 px-2 text-right font-mono">
                    {b.last_tested_rpo_minutes != null && (
                      <span className={clsx('inline-flex items-center gap-1', b.rpo_met ? 'text-emerald' : 'text-rose')}>
                        {b.rpo_met ? <CheckCircle2 size={10} /> : <XCircle size={10} />}
                        {fmtMin(b.last_tested_rpo_minutes)}
                      </span>
                    )}
                  </td>
                  <td className={clsx('py-1.5 px-2 text-[10px]', FRESH_TONE[b.freshness])}>
                    {b.last_tested_at?.slice(0, 10) || 'never'}
                    {b.freshness !== 'untested' && <span className="ml-1 uppercase font-bold">({b.freshness})</span>}
                  </td>
                  <td className="py-1.5 px-2 text-right">
                    <button type="button" onClick={() => remove(b.id)} className="text-text-muted hover:text-rose"><Trash2 size={11} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function Tile({ label, value, tone, pulse }: { label: string; value: number; tone: string; pulse?: boolean }) {
  const t = tone === 'rose' ? 'text-rose' : tone === 'amber' ? 'text-amber-light' : tone === 'emerald' ? 'text-emerald' : 'text-blue-light'
  return (
    <div className="card p-3">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={clsx('text-2xl font-bold mt-1 font-mono', t, pulse && 'animate-pulse')}>{value}</div>
    </div>
  )
}
